import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { getErrorMessage } from '../../services/api';
import { Key, Plus, Trash2, Copy, Check, Shield, ToggleLeft, ToggleRight, Eye, EyeOff } from 'lucide-react';

const APIKeySettings = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revealedKeys, setRevealedKeys] = useState({});
  const [copiedRowId, setCopiedRowId] = useState(null);

  const fetchKeys = useCallback(async () => {
    try {
      const res = await api.get('/api-keys');
      setKeys(res.data);
    } catch (err) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    try {
      const res = await api.post('/api-keys', { name: newKeyName, permissions: ['leads', 'properties', 'analytics', 'ota'] });
      setCreatedKey(res.data.key);
      setNewKeyName('');
      fetchKeys();
    } catch (err) { alert(getErrorMessage(err)); }
  };

  const handleRevoke = async (keyId, name) => {
    if (!window.confirm(`Revoke API key "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api-keys/${keyId}`);
      setRevealedKeys(prev => { const n = { ...prev }; delete n[keyId]; return n; });
      fetchKeys();
    } catch (err) { alert(getErrorMessage(err)); }
  };

  const handleToggle = async (keyId) => {
    try {
      await api.put(`/api-keys/${keyId}/toggle`);
      fetchKeys();
    } catch (err) { alert(getErrorMessage(err)); }
  };

  const safeCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    }
  };

  const copyCreatedKey = async () => {
    await safeCopy(createdKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleReveal = async (keyId) => {
    if (revealedKeys[keyId]) {
      setRevealedKeys(prev => { const n = { ...prev }; delete n[keyId]; return n; });
    } else {
      try {
        const res = await api.get(`/api-keys/${keyId}/reveal`);
        setRevealedKeys(prev => ({ ...prev, [keyId]: res.data.key }));
      } catch (err) { alert(getErrorMessage(err)); }
    }
  };

  const copyRowKey = async (keyId) => {
    let fullKey = revealedKeys[keyId];
    if (!fullKey) {
      try {
        const res = await api.get(`/api-keys/${keyId}/reveal`);
        fullKey = res.data.key;
      } catch (err) { alert(getErrorMessage(err)); return; }
    }
    await safeCopy(fullKey);
    setCopiedRowId(keyId);
    setTimeout(() => setCopiedRowId(null), 2000);
  };

  if (loading) return <div className="p-6 text-gray-500">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="api-key-settings">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">API Keys</h2>
          <p className="text-sm text-gray-500">Manage API keys for external integrations</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm" data-testid="create-api-key-btn">
          <Plus size={16} /> Generate API Key
        </button>
      </div>

      {showCreate && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <h3 className="font-medium text-gray-900 mb-3">Generate New API Key</h3>
          <div className="flex gap-3">
            <input type="text" value={newKeyName} onChange={e => setNewKeyName(e.target.value)} placeholder="Key name (e.g., Production Integration)" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none" data-testid="api-key-name-input" />
            <button onClick={handleCreate} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm" data-testid="generate-key-btn">Generate</button>
            <button onClick={() => { setShowCreate(false); setCreatedKey(null); }} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
          </div>
        </div>
      )}

      {createdKey && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="created-key-banner">
          <div className="flex items-center gap-2 mb-2">
            <Shield size={16} className="text-green-600" />
            <span className="font-medium text-green-800 text-sm">API Key Created — Copy it now, it won't be shown again</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white px-3 py-2 rounded border border-green-300 text-sm font-mono text-gray-800 select-all" data-testid="created-key-value">{createdKey}</code>
            <button onClick={copyCreatedKey} className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm" data-testid="copy-key-btn">
              {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
            </button>
          </div>
        </div>
      )}

      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <h4 className="font-medium text-gray-800 text-sm mb-2">Usage</h4>
        <code className="text-xs text-gray-600 bg-white px-2 py-1 rounded border">
          curl -H "X-API-Key: sk_superhost_xxx" {window.location.origin}/api/external/leads
        </code>
        <p className="text-xs text-gray-500 mt-2">Endpoints: /api/external/leads, /api/external/properties, /api/external/lifecycle, /api/external/health</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Key</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Created</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Last Used</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {keys.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                  <Key size={24} className="mx-auto mb-2 text-gray-300" />
                  <p>No API keys yet</p>
                </td>
              </tr>
            ) : (
              keys.map(k => (
                <tr key={k.id} className="hover:bg-gray-50" data-testid={`api-key-row-${k.id}`}>
                  <td className="px-4 py-3 font-medium text-gray-800">{k.name}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <code className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded font-mono" data-testid={`key-display-${k.id}`}>
                        {revealedKeys[k.id] || k.key_preview}
                      </code>
                      <button
                        onClick={() => toggleReveal(k.id)}
                        className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
                        title={revealedKeys[k.id] ? 'Hide key' : 'Show full key'}
                        data-testid={`reveal-key-${k.id}`}
                      >
                        {revealedKeys[k.id] ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                      <button
                        onClick={() => copyRowKey(k.id)}
                        className={`p-1 rounded transition-colors ${copiedRowId === k.id ? 'text-green-600 bg-green-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'}`}
                        title="Copy API key"
                        data-testid={`copy-row-key-${k.id}`}
                      >
                        {copiedRowId === k.id ? <Check size={15} /> : <Copy size={15} />}
                      </button>
                      {copiedRowId === k.id && (
                        <span className="text-xs text-green-600 font-medium" data-testid={`copy-confirm-${k.id}`}>API key copied</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${k.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {k.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{k.created_at ? new Date(k.created_at).toLocaleDateString() : '-'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{k.last_used ? new Date(k.last_used).toLocaleDateString() : 'Never'}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => handleToggle(k.id)} className="p-1.5 rounded hover:bg-gray-100" title={k.is_active ? 'Deactivate' : 'Activate'} data-testid={`toggle-key-${k.id}`}>
                        {k.is_active ? <ToggleRight size={18} className="text-green-600" /> : <ToggleLeft size={18} className="text-gray-400" />}
                      </button>
                      <button onClick={() => handleRevoke(k.id, k.name)} className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Revoke" data-testid={`revoke-key-${k.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default APIKeySettings;
